import React, { useState, useEffect, useLayoutEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  FaChevronDown,
  FaSearch,
  FaBars,
  FaTimes,
  FaChevronRight,
} from "react-icons/fa";
import {
  FaWhatsapp,
  FaFacebookF,
  FaYoutube,
  FaXTwitter,
} from "react-icons/fa6";
import defaultLogo from "../../assets/new_logo_circle.png";
import SmartSearch from "./SmartSearch";
import { useSiteSettings } from "../../contexts/SiteSettingsContext";
import { getImageUrl } from "../../utils/getImageUrl";
import cmsApi from "../../services/cmsApi";

const InstagramIcon = ({ className: cls }) => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className={cls} aria-hidden>
    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
  </svg>
);

/** "JR INSTITUTE OF INFORMATION" + line break + "TECHNOLOGY" for mid-desktop nav only */
function splitSiteNameBeforeTechnology(siteName) {
  const m = (siteName || "").trim().match(/^(.*?)\s+(TECHNOLOGY)$/i);
  return m ? [m[1].trim(), m[2]] : null;
}

const Navbar = () => {
  const navigate = useNavigate();
  const settings = useSiteSettings();
  const siteNameTechnologySplit = splitSiteNameBeforeTechnology(settings.siteName);
  const logo = settings.logo ? getImageUrl(settings.logo) : defaultLogo;
  const handleLogoError = (event) => {
    if (event.currentTarget.src !== defaultLogo) {
      event.currentTarget.src = defaultLogo;
    }
  };
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeMobileDropdown, setActiveMobileDropdown] = useState(null);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [isSmartSearchOpen, setIsSmartSearchOpen] = useState(false);

  const FALLBACK_COURSES = [
    { name: 'Information Technology', slug: 'information-technology' },
    { name: 'Cybersecurity', slug: 'cyber-security' },
    { name: 'Business Administration', slug: 'business-administration' },
    { name: 'Travel & Tourism Management', slug: 'travel-tourism-management' },
    { name: 'Professional Course', slug: 'professional-courses' },
    { name: 'Short Courses', slug: 'short-courses' },
  ];
  const [courseLinks, setCourseLinks] = useState(FALLBACK_COURSES);

  const SPECIAL_COURSES = [
    { name: 'Professional Course', slug: 'professional-courses' },
    { name: 'Short Courses', slug: 'short-courses' },
  ];

  useEffect(() => {
    let cancelled = false;
    const loadCourses = async () => {
      try {
        const result = await cmsApi.getDepartments();
        if (!cancelled && result.success && result.departments?.length) {
          const slugMap = {
            'professional-course': 'professional-courses',
            'short-course': 'short-courses',
          };
          const mapped = result.departments.map(d => ({
            name: d.name,
            slug: slugMap[d.slug] || d.slug,
          }));
          // Append Professional Course and Short Courses (they're custom pages, not in departments table)
          setCourseLinks([...mapped, ...SPECIAL_COURSES]);
        }
      } catch (err) {
        console.warn('Failed to load courses for nav:', err);
      }
    };
    loadCourses();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Refs for dropdown containers
  const aboutDropdownRef = useRef(null);
  const coursesDropdownRef = useRef(null);
  const galleryDropdownRef = useRef(null);
  const newsDropdownRef = useRef(null);
  const mobileMenuRef = useRef(null);
  const mobileTopBarRef = useRef(null);
  const [mobileMenuMaxHeight, setMobileMenuMaxHeight] = useState(null);

  // Mobile menu: cap height and scroll inside so long lists + large system fonts stay reachable
  useLayoutEffect(() => {
    if (!isMobileMenuOpen) return;
    const bar = mobileTopBarRef.current;
    if (!bar) return;
    const update = () => {
      const h = bar.getBoundingClientRect().height;
      setMobileMenuMaxHeight(`calc(100dvh - ${h}px)`);
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(bar);
    window.addEventListener("resize", update);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", update);
    };
  }, [isMobileMenuOpen]);

  useEffect(() => {
    if (!isMobileMenuOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [isMobileMenuOpen]);

  // Desktop hover timers for delayed hide
  const hoverTimers = useRef({});

  const showDropdown = (dropdown) => {
    // Clear any pending hide timers for this dropdown
    if (hoverTimers.current[dropdown]) {
      clearTimeout(hoverTimers.current[dropdown]);
      hoverTimers.current[dropdown] = null;
    }
    setActiveDropdown(dropdown);
  };

  const scheduleHideDropdown = (dropdown, delay = 350) => {
    if (hoverTimers.current[dropdown])
      clearTimeout(hoverTimers.current[dropdown]);
    hoverTimers.current[dropdown] = setTimeout(() => {
      // Only hide if the currently open dropdown matches
      setActiveDropdown((current) => (current === dropdown ? null : current));
      hoverTimers.current[dropdown] = null;
    }, delay);
  };

  const toggleMobileMenu = () => {
    if (!isMobileMenuOpen) closeSmartSearch(); // always close search before opening menu
    setIsMobileMenuOpen(!isMobileMenuOpen);
    setActiveMobileDropdown(null);
  };

  const toggleMobileDropdown = (dropdown) => {
    setActiveMobileDropdown(
      activeMobileDropdown === dropdown ? null : dropdown,
    );
  };

  const toggleSearch = () => {
    setIsSearchOpen(!isSearchOpen);
    setIsSmartSearchOpen(!isSmartSearchOpen);
    if (!isSearchOpen) {
      setSearchTerm("");
    }
  };

  const closeSmartSearch = () => {
    setIsSmartSearchOpen(false);
    setIsSearchOpen(false);
    setSearchTerm("");
  };

  // Handle click outside to close dropdowns
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Check desktop dropdowns
      if (
        aboutDropdownRef.current &&
        !aboutDropdownRef.current.contains(event.target) &&
        coursesDropdownRef.current &&
        !coursesDropdownRef.current.contains(event.target) &&
        galleryDropdownRef.current &&
        !galleryDropdownRef.current.contains(event.target) &&
        newsDropdownRef.current &&
        !newsDropdownRef.current.contains(event.target)
      ) {
        setActiveDropdown(null);
      }

      // Check mobile menu
      if (
        mobileMenuRef.current &&
        !mobileMenuRef.current.contains(event.target)
      ) {
        // Don't close mobile menu if clicking on the menu button itself
        const menuButton = event.target.closest(".mobile-menu-button");
        if (!menuButton) {
          setIsMobileMenuOpen(false);
          setActiveMobileDropdown(null);
        }
      }
    };

    // Handle keyboard shortcuts
    const handleKeyDown = (event) => {
      // Ctrl+K or Cmd+K for search
      if ((event.ctrlKey || event.metaKey) && event.key === 'k') {
        event.preventDefault();
        toggleSearch();
      }
      // Escape to close search
      if (event.key === 'Escape' && isSmartSearchOpen) {
        closeSmartSearch();
      }
    };

    // Add event listeners
    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleKeyDown);

    // Cleanup
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleKeyDown);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSmartSearchOpen]);

  return (
    <header className="sticky top-0 z-50 animate-slide-down">
      {/* Desktop Navigation Bar */}
      <div className="bg-white p-4 h-32 items-center relative shadow-lg hidden lg:flex">
        <div className="text-custom-pink font-bold text-1xl flex min-w-[11rem] shrink-0 items-center gap-3 pl-2 lg:min-w-[12rem] lg:pl-3 xl:min-w-0 xl:max-w-md xl:flex-1 xl:pl-2">
          {/* Fixed-width box so wide/tall logos stay fully visible (object-contain); nav column offset matches headerRightBottomWrapper */}
          <div className="flex h-24 w-[7.75rem] min-w-[7.75rem] shrink-0 items-center justify-center lg:w-[8.25rem] lg:min-w-[8.25rem]">
            <img
              src={logo}
              alt="logo"
              onError={handleLogoError}
              className="hidden max-h-full w-full max-w-full object-contain object-left lg:block"
            />
          </div>
          {/* Cramped lg–xl: hide branding; logo-only saves horizontal space (see headerRightBottomWrapper width) */}
          <span className="hidden min-w-0 w-full flex-1 text-center tracking-wider text-sm xl:ml-0 xl:block xl:text-base min-[1280px]:max-[1810px]:text-xs min-[1280px]:max-[1810px]:tracking-wide 2xl:ml-4 2xl:text-sm 3xl:text-lg">
            {siteNameTechnologySplit ? (
              <>
                <span className="hidden min-[1280px]:max-[1810px]:block">
                  <span className="block">{siteNameTechnologySplit[0]}</span>
                  <span className="block">{siteNameTechnologySplit[1]}</span>
                </span>
                <span className="min-[1280px]:max-[1810px]:hidden">{settings.siteName}</span>
              </>
            ) : (
              settings.siteName
            )}
            <span className="block border-b border-gold px-2 py-1"></span>
            <span className="block max-w-full whitespace-normal break-words px-0.5 py-2 text-center text-xs tracking-widest xl:text-sm min-[1280px]:max-[1810px]:text-[11px] min-[1280px]:max-[1810px]:leading-snug min-[1280px]:max-[1810px]:tracking-wide 2xl:text-xs">
              {settings.siteTagline}
            </span>
          </span>
        </div>

        {/* xl:w-9/12 leaves only 25% width for branding; max-w-md branding (28rem) overflows and is clipped under this column — reserve full 28rem in the tight mid-desktop band */}
        <div className="headerRightBottomWrapper absolute top-0 right-0 flex h-full w-[calc(100%-12rem)] min-w-0 flex-col xl:w-9/12 min-[1280px]:max-[1810px]:!w-[calc(100%-28rem)] min-[1811px]:w-9/12">
          <div className="rounded-bl-xl bg-custom-pink px-3 py-2 flex justify-between items-center">
            <div className="headerBottomLinks ml-1 flex min-w-0 flex-wrap items-center gap-x-4 gap-y-1.5 text-[11px] leading-tight text-white xl:ml-3 xl:flex-nowrap xl:gap-x-6 xl:text-sm xl:leading-normal 2xl:gap-x-8 [&_a]:inline-block [&_a]:whitespace-nowrap [&_a]:py-0.5">
              {/* HIDDEN 2026-08-04 at client request — unhide by restoring these two Links.
              <Link to="/academics" className="text-white hover:text-gold">
                Academics
              </Link>
              <Link to="/departments" className="text-white hover:text-gold">
                Departments
              </Link>
              */}
              {/* HIDDEN 2026-08-04 at client request — unhide by restoring this Link.
              <Link to="/library" className="text-white hover:text-gold">
                Library
              </Link>
              */}
              <Link to="/services" className="text-white hover:text-gold">
                Services
              </Link>
              <Link to="/athletics" className="text-white hover:text-gold">
                Athletics
              </Link>
              <Link to="/feedback" className="text-white hover:text-gold">
                Feedback
              </Link>
              <Link to="/campus-tour" className="text-white hover:text-gold">
                Campus Tour
              </Link>
            </div>
          </div>

          <div className="bg-white flex flex-1 items-center justify-between px-3 xl:px-6">
            <nav
              className="main-nav-links flex min-w-0 flex-1 flex-wrap items-center justify-start gap-x-4 gap-y-2 text-center text-[11px] leading-snug text-gray-700 xl:flex-nowrap xl:gap-x-5 xl:gap-y-0 xl:text-sm xl:leading-normal 2xl:gap-x-7 [&>a]:inline-block [&>a]:whitespace-nowrap [&>a]:px-0.5 [&>a]:py-1 [&>div]:inline-block [&>div]:whitespace-nowrap [&>div]:px-0.5"
              aria-label="Main navigation"
            >
              <Link to="/" className="relative group">
                Home
                <span className="absolute -bottom-4 left-0 w-0 h-[2px] bg-gold transition-all duration-300 group-hover:w-full"></span>
              </Link>

              {/* About Us Dropdown */}
              <div
                className="relative"
                ref={aboutDropdownRef}
                onMouseEnter={() => showDropdown("about")}
                onMouseLeave={() => scheduleHideDropdown("about", 400)}
              >
                <Link
                  to="/about"
                  className="relative group text-gray-700 flex items-center cursor-pointer"
                  onClick={(e) => {
                    e.preventDefault();
                  }}
                >
                  About Us
                  <span className="absolute -bottom-4 left-0 w-0 h-[2px] bg-gold transition-all duration-300 group-hover:w-full"></span>
                  <FaChevronDown
                    className={`ml-2 w-4 h-4 transition-transform duration-200 ${activeDropdown === "about" ? "rotate-180" : ""
                      }`}
                  />
                </Link>
                {activeDropdown === "about" && (
                  <div className="absolute left-0 top-full bg-white shadow-lg mt-2 rounded w-72 z-50 text-left transition-opacity duration-200 ease-out opacity-100">
                    <Link
                      to="/about"
                      className="group flex items-center justify-between px-4 py-2 text-gray-700 hover:bg-gray-100 whitespace-nowrap"
                    >
                      Our History
                      <FaChevronRight className="text-gold opacity-0 group-hover:opacity-100 transition-opacity duration-200" />
                    </Link>
                    <Link
                      to="/about/mission"
                      className="group flex items-center justify-between px-4 py-2 text-gray-700 hover:bg-gray-100 whitespace-nowrap"
                    >
                      Mission & Vision
                      <FaChevronRight className="text-gold opacity-0 group-hover:opacity-100 transition-opacity duration-200" />
                    </Link>
                    <Link
                      to="/about/trustees"
                      className="group flex items-center justify-between px-4 py-2 text-gray-700 hover:bg-gray-100 whitespace-nowrap"
                    >
                      Advisory Board
                      <FaChevronRight className="text-gold opacity-0 group-hover:opacity-100 transition-opacity duration-200" />
                    </Link>
                    <Link
                      to="/about/terms-conditions"
                      className="group flex items-center justify-between px-4 py-2 text-gray-700 hover:bg-gray-100 whitespace-nowrap"
                    >
                      Terms and Conditions
                      <FaChevronRight className="text-gold opacity-0 group-hover:opacity-100 transition-opacity duration-200" />
                    </Link>
                  </div>
                )}
              </div>

              {/* Programs Dropdown */}
              <div
                className="relative"
                ref={coursesDropdownRef}
                onMouseEnter={() => showDropdown("courses")}
                onMouseLeave={() => scheduleHideDropdown("courses", 400)}
              >
                <Link
                  to="/course/information-technology"
                  className="relative group text-gray-700 flex items-center cursor-pointer"
                  onClick={(e) => {
                    e.preventDefault();
                  }}
                >
                  Programs
                  <span className="absolute -bottom-4 left-0 w-0 h-[2px] bg-gold transition-all duration-300 group-hover:w-full"></span>
                  <FaChevronDown
                    className={`ml-2 w-4 h-4 transition-transform duration-200 ${activeDropdown === "courses" ? "rotate-180" : ""
                      }`}
                  />
                </Link>
                {activeDropdown === "courses" && (
                  <div className="absolute left-0 top-full bg-white shadow-lg mt-2 rounded w-72 z-50 text-left transition-opacity duration-200 ease-out opacity-100">
                    {courseLinks.map((course) => (
                      <Link
                        key={course.slug}
                        to={`/course/${course.slug}`}
                        className="group flex items-center justify-between px-4 py-2 text-gray-700 hover:bg-gray-100 whitespace-nowrap"
                      >
                        {course.name}
                        <FaChevronRight className="text-gold opacity-0 group-hover:opacity-100 transition-opacity duration-200" />
                      </Link>
                    ))}
                  </div>
                )}
              </div>

              {/* Gallery Dropdown */}
              <div
                className="relative"
                ref={galleryDropdownRef}
                onMouseEnter={() => showDropdown("gallery")}
                onMouseLeave={() => scheduleHideDropdown("gallery", 400)}
              >
                <Link
                  to="/gallery/photos"
                  className="relative group text-gray-700 flex items-center cursor-pointer"
                  onClick={(e) => {
                    e.preventDefault();
                  }}
                >
                  Gallery
                  <span className="absolute -bottom-4 left-0 w-0 h-[2px] bg-gold transition-all duration-300 group-hover:w-full"></span>
                  <FaChevronDown
                    className={`ml-2 w-4 h-4 transition-transform duration-200 ${activeDropdown === "gallery" ? "rotate-180" : ""
                      }`}
                  />
                </Link>
                {activeDropdown === "gallery" && (
                  <div className="absolute left-0 top-full bg-white shadow-lg mt-2 rounded w-72 z-50 text-left transition-opacity duration-200 ease-out opacity-100">
                    <Link
                      to="/gallery/photos"
                      className="group flex items-center justify-between px-4 py-2 text-gray-700 hover:bg-gray-100 whitespace-nowrap"
                    >
                      Photo Gallery
                      <FaChevronRight className="text-gold opacity-0 group-hover:opacity-100 transition-opacity duration-200" />
                    </Link>
                    <Link
                      to="/gallery/videos"
                      className="group flex items-center justify-between px-4 py-2 text-gray-700 hover:bg-gray-100 whitespace-nowrap"
                    >
                      Video Gallery
                      <FaChevronRight className="text-gold opacity-0 group-hover:opacity-100 transition-opacity duration-200" />
                    </Link>
                  </div>
                )}
              </div>

              {/* News & Events Dropdown */}
              <div
                className="relative"
                ref={newsDropdownRef}
                onMouseEnter={() => showDropdown("news")}
                onMouseLeave={() => scheduleHideDropdown("news", 400)}
              >
                <Link
                  to="/news"
                  className="relative group text-gray-700 flex items-center cursor-pointer"
                  onClick={(e) => {
                    e.preventDefault();
                  }}
                >
                  News & Events
                  <span className="absolute -bottom-4 left-0 w-0 h-[2px] bg-gold transition-all duration-300 group-hover:w-full"></span>
                  <FaChevronDown
                    className={`ml-2 w-4 h-4 transition-transform duration-200 ${activeDropdown === "news" ? "rotate-180" : ""
                      }`}
                  />
                </Link>
                {activeDropdown === "news" && (
                  <div className="absolute left-0 top-full bg-white shadow-lg mt-2 rounded w-72 z-50 text-left transition-opacity duration-200 ease-out opacity-100">
                    <Link
                      to="/events"
                      className="group flex items-center justify-between px-4 py-2 text-gray-700 hover:bg-gray-100 whitespace-nowrap"
                    >
                      All Events
                      <FaChevronRight className="text-gold opacity-0 group-hover:opacity-100 transition-opacity duration-200" />
                    </Link>
                    <Link
                      to="/news"
                      className="group flex items-center justify-between px-4 py-2 text-gray-700 hover:bg-gray-100 whitespace-nowrap"
                    >
                      All News
                      <FaChevronRight className="text-gold opacity-0 group-hover:opacity-100 transition-opacity duration-200" />
                    </Link>
                    {/*                    <Link
                      to="/blog"
                      className="group flex items-center justify-between px-4 py-2 text-gray-700 hover:bg-gray-100 whitespace-nowrap"
                    >
                      Blog
                      <FaChevronRight className="text-gold opacity-0 group-hover:opacity-100 transition-opacity duration-200" />
                    </Link> */}
                  </div>
                )}
              </div>

              <Link to="/admissions" className="relative group text-gray-700">
                Admissions
                <span className="absolute -bottom-4 left-0 w-0 h-[2px] bg-gold transition-all duration-300 group-hover:w-full"></span>
              </Link>
              <Link to="/fees" className="relative group text-gray-700">
                Fee Structure
                <span className="absolute -bottom-4 left-0 w-0 h-[2px] bg-gold transition-all duration-300 group-hover:w-full"></span>
              </Link>
              <Link to="/contact" className="relative group text-gray-700">
                Contact Us
                <span className="absolute -bottom-4 left-0 w-0 h-[2px] bg-gold transition-all duration-300 group-hover:w-full"></span>
              </Link>
            </nav>
            <div className="flex shrink-0 items-center gap-2 [@media(min-width:1024px)_and_(max-width:1069px)]:gap-1.5 xl:gap-4 min-[1280px]:max-[1440px]:!gap-1.5">
              <div className="relative min-[1280px]:max-[1320px]:hidden">
                <button
                  type="button"
                  onClick={toggleSearch}
                  aria-label="Open search"
                  className="group relative overflow-hidden rounded-2xl border border-custom-pink/20 bg-custom-pink/10 px-3 py-2 backdrop-blur-xl transition-all duration-300 hover:scale-105 hover:bg-custom-pink/20 hover:shadow-2xl hover:shadow-custom-pink/10 active:scale-95 [@media(min-width:1024px)_and_(max-width:1069px)]:rounded-xl [@media(min-width:1024px)_and_(max-width:1069px)]:px-2 [@media(min-width:1024px)_and_(max-width:1069px)]:py-1.5 xl:px-4 min-[1280px]:max-[1440px]:!rounded-xl min-[1280px]:max-[1440px]:!px-2 min-[1280px]:max-[1440px]:!py-1.5"
                  style={{
                    background: 'linear-gradient(135deg, rgba(147, 51, 234, 0.1) 0%, rgba(147, 51, 234, 0.05) 100%)',
                    boxShadow: '0 8px 32px rgba(0,0,0,0.1), inset 0 1px 0 rgba(147, 51, 234, 0.2)',
                  }}
                >
                  {/* Liquid Glass Effect */}
                  <div 
                    className="absolute inset-0 rounded-2xl opacity-30 transition-opacity duration-300 group-hover:opacity-50 [@media(min-width:1024px)_and_(max-width:1069px)]:rounded-xl min-[1280px]:max-[1440px]:rounded-xl"
                    style={{
                      background: 'linear-gradient(135deg, rgba(147, 51, 234, 0.2) 0%, rgba(147, 51, 234, 0.1) 50%, rgba(147, 51, 234, 0.05) 100%)',
                    }}
                  />
                  
                  {/* Ctrl+K hidden 1024–1069px and 1280–1440px (icon-only); icon always */}
                  <div className="relative flex items-center gap-1.5 text-custom-pink xl:gap-2 min-[1280px]:max-[1440px]:!gap-0">
                    <FaSearch className="h-4 w-4 shrink-0 transition-transform duration-300 group-hover:scale-110 [@media(min-width:1024px)_and_(max-width:1069px)]:h-3.5 [@media(min-width:1024px)_and_(max-width:1069px)]:w-3.5 min-[1280px]:max-[1440px]:h-3.5 min-[1280px]:max-[1440px]:w-3.5" />
                    <span className="hidden text-xs font-medium transition-all duration-300 group-hover:text-gold lg:inline [@media(min-width:1024px)_and_(max-width:1069px)]:!hidden min-[1280px]:max-[1440px]:!hidden xl:text-sm">
                      Ctrl+K
                    </span>
                  </div>
                  
                  {/* Shimmer Effect */}
                  <div 
                    className="absolute inset-0 -translate-x-full group-hover:translate-x-full transition-transform duration-700 ease-out"
                    style={{
                      background: 'linear-gradient(90deg, transparent, rgba(147, 51, 234, 0.3), transparent)',
                    }}
                  />
                </button>
                
                {isSmartSearchOpen && (
                  <SmartSearch
                    isOpen={isSmartSearchOpen}
                    onClose={closeSmartSearch}
                    searchTerm={searchTerm}
                    setSearchTerm={setSearchTerm}
                  />
                )}
              </div>
              
              <a
                href="https://apply.jrsys.ac.tz/"
                target="_blank"
                rel="noreferrer"
                className="apply-button shrink-0 rounded-3xl bg-gold px-3 py-1.5 text-sm text-white hover:bg-custom-pink [@media(min-width:1024px)_and_(max-width:1069px)]:rounded-2xl [@media(min-width:1024px)_and_(max-width:1069px)]:px-2.5 [@media(min-width:1024px)_and_(max-width:1069px)]:py-1 [@media(min-width:1024px)_and_(max-width:1069px)]:text-xs xl:px-4 xl:py-2 xl:text-base min-[1280px]:max-[1440px]:!rounded-2xl min-[1280px]:max-[1440px]:!px-2 min-[1280px]:max-[1440px]:!py-1 min-[1280px]:max-[1440px]:!text-xs"
              >
                Apply Now
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Navigation Bar */}
      <div
        ref={mobileTopBarRef}
        className="lg:hidden flex justify-between items-center bg-custom-pink text-white p-4"
      >
        <img
          src={logo}
          alt="logo"
          onError={handleLogoError}
          className="w-24 h-24 object-contain"
        />
        {/* Tablet (md+): site name */}
        <div className="hidden min-w-0 flex-1 px-0.5 text-center md:block lg:hidden">
          <div className="min-w-0 whitespace-nowrap font-bold leading-tight tracking-wide md:max-[910px]:text-base md:max-[910px]:leading-snug md:max-[910px]:tracking-wide min-[911px]:text-xl min-[911px]:leading-snug min-[911px]:tracking-wider">
            {settings.siteName}
          </div>
          <div className="my-1 flex justify-center md:my-1.5">
            <div
              className="rounded-full bg-gold md:max-[910px]:h-[3px] md:max-[910px]:w-24 min-[911px]:h-0.5 min-[911px]:w-28"
              aria-hidden
            />
          </div>
          <div className="min-w-0 whitespace-nowrap font-bold leading-tight tracking-wide md:max-[910px]:text-base md:max-[910px]:leading-snug md:max-[910px]:tracking-wide min-[911px]:text-xl min-[911px]:leading-snug min-[911px]:tracking-wider">
            {settings.siteTagline}
          </div>
        </div>

        {/* Mobile only: cinematic location stacked on top of the social icons */}
        <div className="flex md:hidden flex-1 flex-col items-center justify-center gap-1.5 px-1">
          {/* Dramatic location label */}
          <div className="flex items-center gap-1">
            <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden
              className="w-3 h-3 text-gold drop-shadow-[0_0_4px_rgba(252,169,3,0.7)]">
              <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5a2.5 2.5 0 110-5 2.5 2.5 0 010 5z" />
            </svg>
            <span className="text-[11px] font-extrabold uppercase tracking-[0.18em] whitespace-nowrap bg-gradient-to-r from-gold via-amber-200 to-gold bg-clip-text text-transparent drop-shadow-[0_0_6px_rgba(252,169,3,0.55)]">
              Sakina&nbsp;&middot;&nbsp;Arusha
            </span>
          </div>
          {/* Social icons */}
          <div className="flex items-center justify-center gap-1.5">
            <a href={settings.whatsappUrl || "https://wa.me/255754360590"} target="_blank" rel="noreferrer" aria-label="WhatsApp"
              className="w-6 h-6 rounded-full bg-[#25D366] text-white flex items-center justify-center shadow-sm hover:scale-110 transition-transform">
              <FaWhatsapp className="w-3 h-3" />
            </a>
            <a href={settings.facebookUrl || "https://www.facebook.com/jrinstitute"} target="_blank" rel="noreferrer" aria-label="Facebook"
              className="w-6 h-6 rounded-full bg-[#1877F2] text-white flex items-center justify-center shadow-sm hover:scale-110 transition-transform">
              <FaFacebookF className="w-2.5 h-2.5" />
            </a>
            <a href={settings.instagramUrl || "https://www.instagram.com/jrinstituteoftechnology"} target="_blank" rel="noreferrer" aria-label="Instagram"
              className="w-6 h-6 rounded-full text-white flex items-center justify-center shadow-sm hover:scale-110 transition-transform"
              style={{ background: 'linear-gradient(45deg,#feda75,#fa7e1e,#d62976,#962fbf,#4f5bd5)' }}>
              <InstagramIcon cls="w-2.5 h-2.5" />
            </a>
            <a href={settings.youtubeUrl || "https://www.youtube.com/channel/UCM6PtxyjAzWV8UiwbqvkMnw"} target="_blank" rel="noreferrer" aria-label="YouTube"
              className="w-6 h-6 rounded-full bg-[#FF0000] text-white flex items-center justify-center shadow-sm hover:scale-110 transition-transform">
              <FaYoutube className="w-3 h-3" />
            </a>
            <a href={settings.twitterUrl || "https://twitter.com/JriitTechnology"} target="_blank" rel="noreferrer" aria-label="X"
              className="w-6 h-6 rounded-full bg-black text-white flex items-center justify-center shadow-sm hover:scale-110 transition-transform">
              <FaXTwitter className="w-2.5 h-2.5" />
            </a>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {/* Search icon — visible on mobile and tablet */}
          <button
            type="button"
            onClick={toggleSearch}
            aria-label="Search"
            className="lg:hidden flex items-center justify-center w-9 h-9 rounded-full bg-white/20 hover:bg-white/30 text-white transition-colors"
          >
            <FaSearch className="w-4 h-4" />
          </button>
          <a
            href="https://apply.jrsys.ac.tz/"
            target="_blank"
            rel="noreferrer"
            className="hidden md:block 2xl:hidden bg-gold text-white px-3 py-1.5 rounded-full text-sm hover:bg-white hover:text-custom-pink transition-colors"
          >
            Apply Now
          </a>
          <div
            onClick={toggleMobileMenu}
            className="mobile-menu-button cursor-pointer text-3xl space-x-2 flex items-center"
          >
            {isMobileMenuOpen ? <FaTimes /> : <FaBars />}
            <span className="hidden md:inline text-lg">
              {isMobileMenuOpen ? "Close" : "Menu"}
            </span>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div
          className="lg:hidden bg-white text-left p-4 space-y-2 shadow-md overflow-y-auto overscroll-y-contain pb-[max(1rem,env(safe-area-inset-bottom))] [-webkit-overflow-scrolling:touch]"
          style={{
            maxHeight:
              mobileMenuMaxHeight ?? "calc(100dvh - 9rem)",
          }}
          ref={mobileMenuRef}
        >
          <Link
            to="/"
            onClick={() => setIsMobileMenuOpen(false)}
            className="block text-gray-700 py-2 border-b border-gray-200"
          >
            Home
          </Link>

          {/* Mobile About Us Dropdown */}
          <div>
            <div
              className="flex items-center justify-between text-gray-700 py-2 border-b border-gray-200 cursor-pointer"
              onClick={() => toggleMobileDropdown("about")}
            >
              About Us
              <FaChevronDown
                className={`w-4 h-4 transition-transform duration-200 ${activeMobileDropdown === "about" ? "rotate-180" : ""
                  }`}
              />
            </div>
            {activeMobileDropdown === "about" && (
              <div className="ml-4 mt-2 space-y-1">
                <Link to="/about" onClick={() => setIsMobileMenuOpen(false)} className="block text-gray-600 py-1 text-sm">
                  Our History
                </Link>
                <Link
                  to="/about/mission"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="block text-gray-600 py-1 text-sm"
                >
                  Mission & Vision
                </Link>
                <Link
                  to="/about/trustees"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="block text-gray-600 py-1 text-sm"
                >
                  Advisory Board
                </Link>
                <Link
                  to="/about/terms-conditions"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="block text-gray-600 py-1 text-sm"
                >
                  Terms and Conditions
                </Link>
              </div>
            )}
          </div>

          {/* Mobile Programs Dropdown */}
          <div>
            <div
              className="flex items-center justify-between text-gray-700 py-2 border-b border-gray-200 cursor-pointer"
              onClick={() => toggleMobileDropdown("courses")}
            >
              Programs
              <FaChevronDown
                className={`w-4 h-4 transition-transform duration-200 ${activeMobileDropdown === "courses" ? "rotate-180" : ""
                  }`}
              />
            </div>
            {activeMobileDropdown === "courses" && (
              <div className="ml-4 mt-2 space-y-1">
                {courseLinks.map((course) => (
                  <Link key={course.slug} to={`/course/${course.slug}`} onClick={() => setIsMobileMenuOpen(false)} className="block text-gray-600 py-1 text-sm">{course.name}</Link>
                ))}
              </div>
            )}
          </div>

          {/* Mobile Gallery Dropdown */}
          <div>
            <div
              className="flex items-center justify-between text-gray-700 py-2 border-b border-gray-200 cursor-pointer"
              onClick={() => toggleMobileDropdown("gallery")}
            >
              Gallery
              <FaChevronDown
                className={`w-4 h-4 transition-transform duration-200 ${activeMobileDropdown === "gallery" ? "rotate-180" : ""
                  }`}
              />
            </div>
            {activeMobileDropdown === "gallery" && (
              <div className="ml-4 mt-2 space-y-1">
                <Link to="/gallery/photos" onClick={() => setIsMobileMenuOpen(false)} className="block text-gray-600 py-1 text-sm">Photo Gallery</Link>
                <Link to="/gallery/videos" onClick={() => setIsMobileMenuOpen(false)} className="block text-gray-600 py-1 text-sm">Video Gallery</Link>
              </div>
            )}
          </div>

          {/* Mobile News & Events Dropdown */}
          <div>
            <div
              className="flex items-center justify-between text-gray-700 py-2 border-b border-gray-200 cursor-pointer"
              onClick={() => toggleMobileDropdown("news")}
            >
              News & Events
              <FaChevronDown
                className={`w-4 h-4 transition-transform duration-200 ${activeMobileDropdown === "news" ? "rotate-180" : ""
                  }`}
              />
            </div>
            {activeMobileDropdown === "news" && (
              <div className="ml-4 mt-2 space-y-1">
                <Link to="/events" onClick={() => setIsMobileMenuOpen(false)} className="block text-gray-600 py-1 text-sm">
                  All Events
                </Link>
                <Link to="/news" onClick={() => setIsMobileMenuOpen(false)} className="block text-gray-600 py-1 text-sm">
                  All News
                </Link>
              </div>
            )}
          </div>

          <Link to="/admissions" onClick={() => setIsMobileMenuOpen(false)} className="block text-gray-700 py-2 border-b border-gray-200">Admissions</Link>
          <Link to="/fees" onClick={() => setIsMobileMenuOpen(false)} className="block text-gray-700 py-2 border-b border-gray-200">Fee Structure</Link>
          <Link to="/athletics" onClick={() => setIsMobileMenuOpen(false)} className="block text-gray-700 py-2 border-b border-gray-200">Athletics</Link>
          <Link to="/campus-tour" onClick={() => setIsMobileMenuOpen(false)} className="block text-gray-700 py-2 border-b border-gray-200">Campus Tour</Link>
          <Link to="/contact" onClick={() => setIsMobileMenuOpen(false)} className="block text-gray-700 py-2 border-b border-gray-200">Contact Us</Link>
          {/* HIDDEN 2026-08-04 at client request — unhide by restoring these two Links.
          <Link to="/academics" onClick={() => setIsMobileMenuOpen(false)} className="block text-gray-700 py-2 border-b border-gray-200">Academics</Link>
          <Link to="/departments" onClick={() => setIsMobileMenuOpen(false)} className="block text-gray-700 py-2 border-b border-gray-200">Departments</Link>
          */}
          {/* HIDDEN 2026-08-04 at client request — unhide by restoring this Link.
          <Link
            to="/library"
            onClick={() => setIsMobileMenuOpen(false)}
            className="block text-gray-700 py-2 border-b border-gray-200"
          >
            Library
          </Link>
          */}
          <Link
            to="/services"
            onClick={() => setIsMobileMenuOpen(false)}
            className="block text-gray-700 py-2 border-b border-gray-200"
          >
            Services
          </Link>
          <Link
            to="/feedback"
            onClick={() => setIsMobileMenuOpen(false)}
            className="block text-gray-700 py-2 border-b border-gray-200"
          >
            Feedback
          </Link>

          <a
            href="https://apply.jrsys.ac.tz/"
            target="_blank"
            rel="noreferrer"
            className="block bg-gold text-white py-3 rounded-full text-center mt-4"
          >
            Apply Now
          </a>

          {/* Bottom contact — single number */}
          <a
            href="tel:+255754360590"
            className="mt-3 flex items-center justify-center gap-2 py-2 text-custom-pink font-semibold"
          >
            <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden className="w-4 h-4">
              <path d="M6.62 10.79a15.05 15.05 0 006.59 6.59l2.2-2.2a1 1 0 011.02-.24 11.4 11.4 0 003.57.57 1 1 0 011 1V20a1 1 0 01-1 1A17 17 0 013 4a1 1 0 011-1h3.5a1 1 0 011 1 11.4 11.4 0 00.57 3.57 1 1 0 01-.24 1.02l-2.2 2.2z" />
            </svg>
            +255 754 360 590
          </a>
        </div>
      )}

      {/* Mobile/tablet SmartSearch — click-outside backdrop + fixed dropdown */}
      {isSmartSearchOpen && (
        <div className="lg:hidden">
          {/* Transparent backdrop — clicking anywhere outside closes search */}
          <div
            className="fixed inset-0 z-[9998]"
            onClick={closeSmartSearch}
            aria-hidden
          />
          <SmartSearch
            isOpen={isSmartSearchOpen}
            onClose={closeSmartSearch}
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            className="fixed left-4 right-4 top-32 z-[9999] bg-white rounded-lg shadow-xl border border-gray-200"
          />
        </div>
      )}
    </header>
  );
};

export default Navbar;
